import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Types for admin data
export interface WaitlistEntry {
  id: string;
  email: string;
  business_type: string;
  custom_business_type: string | null;
  created_at: string;
}

export interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  subscription_status: string | null;
  plan_name: string | null;
  plan_display_name: string | null;
  billing_cycle: string | null;
  business_count: number;
  max_businesses: number | null;
  max_employees_per_business: number | null;
}

export interface AdminBusiness {
  id: string;
  name: string;
  user_id: string;
  owner_email: string;
  employee_count: number;
  plan_name: string | null;
  created_at: string;
}

export interface AdminStats {
  totalUsers: number;
  totalBusinesses: number;
  waitlistCount: number;
  activeSubscriptions: number;
}

// Fetch waitlist entries
export function useWaitlist() {
  return useQuery({
    queryKey: ['admin', 'waitlist'],
    queryFn: async (): Promise<WaitlistEntry[]> => {
      const { data, error } = await supabase
        .from('waiting_list')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch all users with subscription info
// Note: This requires an Edge Function to bypass RLS
export function useAllUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async (): Promise<AdminUser[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('admin-get-users', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch all businesses with details
// Note: This requires an Edge Function to bypass RLS
export function useAllBusinesses() {
  return useQuery({
    queryKey: ['admin', 'businesses'],
    queryFn: async (): Promise<AdminBusiness[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('admin-get-businesses', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch admin dashboard stats
export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async (): Promise<AdminStats> => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Admin stats - Session:', session ? 'exists' : 'null', 'Token:', session?.access_token ? 'exists' : 'null', 'Error:', sessionError);

      if (!session?.access_token) {
        throw new Error('No session token available');
      }

      const { data, error } = await supabase.functions.invoke('admin-get-stats', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      return data || {
        totalUsers: 0,
        totalBusinesses: 0,
        waitlistCount: 0,
        activeSubscriptions: 0,
      };
    },
  });
}
